import React from 'react';
import { Container, Row } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaGift, FaHeart, FaShoppingBag, FaSun, FaStar } from 'react-icons/fa';

import Message from '../../components/layout/Message';
import ProductCard from '../../components/product/ProductCard';
import Meta from '../../components/layout/Meta';
import Paginate from '../../components/ui/Paginate';
import { useGetProductsQuery } from '../../slices/productApiSlice';
import { useGetEventByIdQuery } from '../../slices/promotionApiSlice';
import ProductCardSkeleton from '../../components/ui/ProductCardSkeleton';
import ActiveGlobalDiscountBanner from '../../components/ui/ActiveGlobalDiscountBanner';
import ShippingBehaviorBanner from '../../components/ui/ShippingBehaviorBanner';

const PromotionScreen = () => {
    const { promotionId, pageNumber } = useParams();
    const [searchParams] = useSearchParams();
    const sort = searchParams.get('sort') || '';
    
    const { userInfo } = useSelector((state) => state.auth);
    const { data: event, isLoading: loadingEvent } = useGetEventByIdQuery(promotionId);

    const getIcon = (iconName) => {
        switch (iconName) {
            case 'gift': return <FaGift size={40} />;
            case 'heart': return <FaHeart size={40} />;
            case 'shopping-bag': return <FaShoppingBag size={40} />;
            case 'sun': return <FaSun size={40} />;
            default: return <FaStar size={40} />;
        }
    };

    // Prepare keywords string for the query
    const keywordsString = event?.keywords ? event.keywords.join(',') : '';
    const page = pageNumber || 1;

    const {
        data: productsData,
        isLoading: loadingProducts,
        error,
    } = useGetProductsQuery({
        keyword: keywordsString,
        pageNumber: page,
        priceAssigned: userInfo?.priceAssigned || null,
        sort,
    }, {
        skip: !keywordsString
    });

    if (loadingEvent) return (
        <Row className="mx-0">
          <div className="ProductGrid">
            {[...Array(8)].map((_, idx) => (
              <ProductCardSkeleton key={idx} />
            ))}
          </div>
        </Row>
    );
    if (!event) return <Message variant="danger">Evento no encontrado</Message>;

    return (
        <>
            <Meta title={`${event.title.replace('{name}', '')} | HomeSanty`} />
            <ActiveGlobalDiscountBanner allowGuestPrompt />
            <ShippingBehaviorBanner />
            
            {/* Background Ambient Gradient */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: event.gradient,
                    opacity: 0.05,
                    zIndex: -1,
                    pointerEvents: 'none'
                }}
            />

            {/* Hero Section del Evento */}
            <div 
                style={{ 
                    background: event.gradient, 
                    color: '#fff',
                    padding: 'clamp(3rem, 5vw, 5rem) 0',
                    marginBottom: '3rem',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
            >
                <Container>
                    <div className="d-flex align-items-center justify-content-center flex-column text-center position-relative" style={{ zIndex: 2 }}>
                        <div 
                            className="mb-3 p-3 shadow-sm"
                            style={{
                                background: 'rgba(255,255,255,0.2)',
                                borderRadius: '50%',
                                backdropFilter: 'blur(5px)',
                                border: '1px solid rgba(255,255,255,0.3)'
                            }}
                        >
                            {getIcon(event.icon)}
                        </div>
                        <h1 className="fw-bold mb-2" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}>
                            {event.title.replace('{name}', userInfo ? userInfo.name : '')}
                        </h1>
                        <p className="lead mb-0" style={{ opacity: 0.95, maxWidth: '800px' }}>
                            {event.description}
                        </p>
                    </div>
                </Container>

                {/* Elementos decorativos de fondo */}
                <div 
                    style={{
                        position: 'absolute',
                        top: '-20%',
                        left: '-5%',
                        width: '300px',
                        height: '300px',
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '50%',
                        filter: 'blur(40px)'
                    }}
                />
                <div 
                    style={{
                        position: 'absolute',
                        bottom: '-20%',
                        right: '-5%',
                        width: '400px',
                        height: '400px',
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: '50%',
                        filter: 'blur(60px)'
                    }}
                />
            </div>

            <Container className="mb-5">
                {loadingProducts ? (
                    <Row className="mx-0">
                      <div className="ProductGrid">
                        {[...Array(8)].map((_, idx) => (
                          <ProductCardSkeleton key={idx} />
                        ))}
                      </div>
                    </Row>
                ) : error ? (
                    <Message variant="danger">
                        {error?.data?.message || error.error}
                    </Message>
                ) : (
                    <>
                        {productsData?.products?.length === 0 ? (
                            <Message variant="info">
                                No se encontraron productos para esta colección en este momento.
                            </Message>
                        ) : (
                            <>
                                <Row className="mx-0">
                                    <div className="ProductGrid">
                                        {productsData.products.map((product) => (
                                            <ProductCard product={product} key={product._id} />
                                        ))}
                                    </div>
                                </Row>
                                <div className="mt-4">
                                    <Paginate
                                        pages={productsData.pages}
                                        page={productsData.page}
                                        keyword={keywordsString}
                                        sort={sort}
                                        promotionId={promotionId}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
            </Container>
        </>
    );
};

export default PromotionScreen;
